import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";

const title = "Elevhälsoteamet | Swedish International School Estepona";
const description =
  "Skolans elevhälsoteam leds av rektor och består av specialpedagog och psykolog, och arbetar för elevernas hälsa, trygghet och välbefinnande.";

export const Route = createFileRoute("/elevhalsoteamet")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/elevhalsoteamet") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/elevhalsoteamet"),
  }),
  component: Elevhalsoteamet,
});

function Elevhalsoteamet() {
  return (
    <>
      <PageHero
        eyebrow="Trygghet och hälsa"
        title="Elevhälsoteamet"
        intro="Skolan har ett elevhälsoteam som leds av rektor och består av specialpedagog och psykolog."
      />

      <Prose>
        <p>
          Elevhälsoteamet utgör en viktig del av skolans övergripande struktur och är sammansatt av
          kompetenta yrkespersoner som samarbetar för att främja elevernas hälsa och välbefinnande.
          Teamet ger en mångsidig och helhetsorienterad approach för att möta olika behov och
          utmaningar.
        </p>

        <h2>Skolpsykolog</h2>
        <p>
          Skolpsykologen tillhandahåller expertis inom psykologiska aspekter av elevernas utveckling
          och lärande. Genom psykologiska utvärderingar och interventioner stödjer skolpsykologen
          elever som kan ha specifika behov inom det psykologiska området. Skolpsykologen samarbetar
          också med lärare och föräldrar för att skapa en gynnsam miljö för elevernas mentala hälsa.
          Vår skolpsykolog är en extern konsult som tas in vid behov, vilket ger skolan möjlighet att
          dra nytta av psykologens expertis när det krävs.
        </p>

        <h2>Ledning av rektor</h2>
        <p>
          Rektorn spelar en central roll som ledare för elevhälsoteamet. Genom att ge riktlinjer,
          resurser och stöd möjliggör rektorn effektivt arbete och samarbete inom teamet. Rektorn är
          även ansvarig för att integrera elevhälsoperspektivet i skolans övergripande strategier och
          beslut.
        </p>

        <p>
          Detta utgör vårt mångsidiga och samordnade elevhälsoteam som sträcker sig bortom
          undervisningen för att stödja eleverna på olika nivåer och säkerställa en trygg och
          hälsosam skolmiljö. Genom att arbeta tillsammans kan teamet skapa en helhetsbild av
          elevernas behov och bidra till en inkluderande och trivsam skolmiljö.
        </p>

        <h2>Specialpedagog</h2>
        <p>
          Specialpedagogen arbetar nära klasslärarna med kartläggning, anpassningar i undervisningen
          och uppföljning av elever som behöver mer stöd i något ämne. Det kan handla om extra
          träning i läsning och skrivning, om struktur och studieteknik, eller om att hitta rätt nivå
          för en elev som behöver större utmaningar.
        </p>

        <h2>Så får ni kontakt</h2>
        <p>
          Första kontakten går alltid via klassläraren eller rektor, som lyfter frågan vidare till
          teamet. Ni kan också mejla{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">info@sweintschool.com</a>{" "}
          så återkommer skolan med förslag på tid. Elever kan själva vända sig till en vuxen på
          skolan när de behöver prata med någon.
        </p>

        <h2>Sekretess och samarbete med hemmet</h2>
        <p>
          Det som tas upp i elevhälsoarbetet hanteras med respekt för elevens och familjens
          integritet, och information delas bara med dem som behöver den för att kunna ge stöd.
          Vårdnadshavare involveras i insatser som rör det egna barnet. Behöver eleven vård eller
          utredning utanför skolan hjälper vi till att peka vidare, men skolan utför inte sjukvård.
        </p>

        <h2>Förebyggande arbete</h2>
        <p>
          En stor del av elevhälsan handlar om det som sker innan problem uppstår: trygghet på
          rasterna, gruppstärkande arbete i klasserna, tydliga rutiner vid skolstart och samtal om
          hur vi är mot varandra. Läs mer om hur vi tar emot nya familjer på sidan om{" "}
          <a className="text-primary link-quiet" href="/gemenskap">gemenskap</a>.
        </p>
      </Prose>

      <FaqBlock items={faqFor("elevhalsa", "sv")} locale={"sv"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
